import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  V2ContextBar,
  AppliedChip,
  ContextToolbar,
  FiltersBtn,
  FiltersBadge,
  FilterIcon,
  ResetBtn,
  ChipsRow,
  AppliedChipEl,
  ChipCategory,
  AppliedChipX
} from '../components/V2ContextBar';
import { PageHeader } from '../components/PageHeader';
import { TabBar } from '../components/TabBar';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { SearchInput } from '../components/SearchInput';
import { ActivityFeed } from '../components/ActivityFeed';
import type { ActivityItem } from '../components/ActivityFeed';
import { ActivityDetailDrawer } from '../components/ActivityDetailDrawer';
import { useV2Filters } from '../context/V2FiltersContext';
import { useDistributionGlobalDataFilters } from 'apiTyped/hooks/global_data_view.query';
import {
  useV2DistributionPos,
  useV2DistributionPosCount,
  useV2DistributionPosStatusSummary
} from 'apiTyped/hooks/v2.distribution.query';
import type { V2DistributionPosFilter } from 'apiTyped/hooks/v2.distribution.query';
import {
  useCrmPipeline,
  useCreateActionList,
  toCreateActionListBody
} from 'apiTyped/hooks/v2.crm.query';
import {
  PosFilterState,
  DEFAULT_FILTERS,
  countActiveFilters,
  buildProductTypeQueryFields,
  buildSpiritAwardQueryFields
} from '../components/posFilterTypes';
import { PosFilterModal } from '../components/PosFilterModal';
import {
  ColumnDisplayModal,
  ColumnDef
} from '../components/ColumnDisplayModal';
import { Pagination } from '../components/Pagination';
import { PosDrawer } from './CrmSalesTracking/PosDrawer';
import {
  WriteEmailModal,
  SaveActionListModal
} from './CrmSalesTracking/CrmModals';
import type { ActionList, PosEntry, PosStatus } from './CrmSalesTracking/types';

// ─── Helpers ────────────────────────────────────────────────────────────────

type PosTab = 'pos' | 'lists' | 'activity';

const PAGE_SIZE = 25;

/**
 * POS table columns — mirrors the PO's proto (`POS_COLUMNS` in proto.html)
 * for the historical-status / evolution / last-change columns, plus the
 * pre-existing extra columns (market, avg price, WS award, competitor refs)
 * kept available behind the toggle so no prior capability is lost.
 */
const POS_COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'POS', defaultOn: true, always: true },
  { id: 'status', label: 'Current Status', defaultOn: true },
  { id: 'status12m', label: '12 Months', defaultOn: false },
  { id: 'status6m', label: '6 Months', defaultOn: false },
  { id: 'status3m', label: '3 Months', defaultOn: false },
  { id: 'realtime', label: 'Real-time', defaultOn: true },
  { id: 'yourRef', label: 'Your Ref', defaultOn: true },
  { id: 'evolution', label: 'Evolution', defaultOn: true },
  { id: 'refs', label: 'Refs / Offered', defaultOn: true },
  { id: 'competitors', label: 'Competitors', defaultOn: true },
  { id: 'lastChange', label: 'Last Change', defaultOn: true },
  { id: 'market', label: 'Market', defaultOn: false },
  { id: 'avgPrice', label: 'Avg Price', defaultOn: false },
  { id: 'wsAward', label: 'WS Award', defaultOn: false }
];

const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = POS_COLUMNS.reduce(
  (acc, c) => ({ ...acc, [c.id]: c.defaultOn }),
  {} as Record<string, boolean>
);

function evolutionArrow(v: number | null | undefined) {
  if (v === null || v === undefined)
    return { arrow: '→', sign: '', tone: 'flat' as const };
  if (v > 0) return { arrow: '↗', sign: '+', tone: 'up' as const };
  if (v < 0) return { arrow: '↘', sign: '', tone: 'down' as const };
  return { arrow: '→', sign: '±', tone: 'flat' as const };
}

function formatListDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function listProgress(list: ActionList) {
  let towin = 0,
    active = 0,
    lost = 0,
    prospect = 0;
  list.pos.forEach((p) => {
    if (p.currentStatus === 'towin') towin++;
    else if (p.currentStatus === 'active') active++;
    else if (p.currentStatus === 'lost') lost++;
    else if (p.currentStatus === 'prospect') prospect++;
  });
  const toWinTotal = towin + prospect;
  let startNonActive = 0,
    moved = 0;
  list.pos.forEach((p) => {
    if (p.initialStatus !== 'active') {
      startNonActive++;
      if (p.currentStatus === 'active') moved++;
    }
  });
  const successRate =
    startNonActive === 0 ? null : Math.round((moved / startNonActive) * 100);
  return {
    total: list.pos.length,
    toWinTotal,
    active,
    lost,
    successRate,
    moved,
    startNonActive
  };
}

function classifyChange(
  initial: PosStatus,
  current: PosStatus
): { type: 'positive' | 'negative' | 'pending' | 'neutral'; label: string } {
  if (initial === current) return { type: 'neutral', label: 'No change' };
  if (initial === 'lost' && current === 'active')
    return { type: 'positive', label: 'Regained' };
  if ((initial === 'prospect' || initial === 'towin') && current === 'active')
    return { type: 'positive', label: 'Won' };
  if (initial === 'active' && current === 'lost')
    return { type: 'negative', label: 'Lost' };
  if (current === 'lost') return { type: 'negative', label: 'Declined' };
  if (initial === 'lost' && current === 'towin')
    return { type: 'pending', label: "Competitor ID'd" };
  if (initial === 'prospect' && current === 'towin')
    return { type: 'pending', label: 'Progressing' };
  return { type: 'pending', label: 'Status changed' };
}

const statusToVariant = (s?: string | null) =>
  s === 'active' || s === 'lost' || s === 'towin' || s === 'new'
    ? s
    : ('default' as const);

const displayStatus = (status?: string | null) => {
  if (status === 'prospect') return 'To Win';
  if (status === 'unknown') return 'Unknown';
  if (!status) return 'Active';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const drawerStatus = (status?: string | null): PosStatus => {
  if (status === 'active' || status === 'lost' || status === 'prospect') {
    return status;
  }
  return 'prospect';
};

const countRefNames = (value?: string | null) =>
  value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean).length
    : 0;

/**
 * Adapts a real `/v2/distribution/pos` API row into the `PosEntry` shape
 * the shared `PosDrawer` (built for CRM Sales Tracking, per proto's single
 * canonical `openDrawer()`/`WS_openPOSDrawer`) expects, so clicking a POS
 * row here opens the exact same lateral panel as proto.html — synthesizing
 * any fields the live API doesn't yet expose, the same way proto's own
 * `_getOrSynthesizeContact()` fills in a CONTACTS-shaped record from
 * POS_DATA on the fly.
 */
function rowToPosEntry(row: any): PosEntry {
  const id = row.id ?? row.entity_id ?? 0;
  const status = drawerStatus(row.pos_status);
  const clientRefCount = countRefNames(row.client_refs);
  const rivalRefCount = countRefNames(row.rival_refs);
  return {
    id,
    name: row.entity_name || '—',
    type: row.store_primary_sub_type_name || '—',
    city: row.store_city_name || '—',
    region: row.market_name || '—',
    country: row.market_name || '—',
    status,
    network: 'on',
    yourRef: clientRefCount,
    totalRefs: clientRefCount + rivalRefCount,
    competitors: rivalRefCount,
    lastChange: row.last_change || '—',
    evolution: row.evolution ?? 0,
    address: [row.store_city_name, row.market_name].filter(Boolean).join(', '),
    wineListSize: clientRefCount + rivalRefCount,
    avgPrice:
      row.av_price != null
        ? `${row.av_price.toFixed(0)} ${row.displayed_currency || 'USD'}`
        : '—',
    lastChecked: row.last_change || '—',
    shelves: clientRefCount
  };
}

/**
 * V2 Points of Sale page.
 *
 * Uses GET /v2/distribution/pos with the shared V2 context filters
 * (channel, brand, market) applied. Shows a searchable data table
 * of all points of sale for the user's distribution.
 */
export const V2PointsOfSale: React.FC = () => {
  const { filters, isLoading: isLoadingContext } = useV2Filters();
  const { isLoadingFilters: isLoadingFilterOptions, filters: apiFilters } =
    useDistributionGlobalDataFilters();

  // Local POS filter state
  const [posFilters, setPosFilters] = useState<PosFilterState>(DEFAULT_FILTERS);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Column display (new — mirrors proto's posColDisplayModal)
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(DEFAULT_COLUMN_VISIBILITY);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const visibleColumnCount =
    Object.values(columnVisibility).filter(Boolean).length;

  // Pagination (new — mirrors proto's pos-pager)
  const [page, setPage] = useState(1);
  const handleFilterChange = useCallback(
    (updater: (prev: PosFilterState) => PosFilterState) =>
      setPosFilters(updater),
    []
  );
  const handleFilterReset = useCallback(
    () => setPosFilters(DEFAULT_FILTERS),
    []
  );

  const activeFilterCount = useMemo(
    () => countActiveFilters(posFilters, filters.channel === 'on_trade'),
    [posFilters, filters.channel]
  );

  // CRM data (action lists, activity, pipeline POS)
  const {
    pos: crmPos,
    actionLists,
    activity,
    refetch: refetchCrmPipeline
  } = useCrmPipeline({
    market: filters.market,
    channel: filters.channel,
    brand: filters.brand
  });
  const createActionList = useCreateActionList();

  // Tab + selection state
  const [activeTab, setActiveTab] = useState<PosTab>('pos');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [activityUserFilter, setActivityUserFilter] = useState('all');
  const [activityDetailItem, setActivityDetailItem] =
    useState<ActivityItem | null>(null);
  const [activityDetailOpen, setActivityDetailOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [actionListModalOpen, setActionListModalOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<ActionList | null>(null);
  const [listDetailOpen, setListDetailOpen] = useState(false);
  const [selectedPos, setSelectedPos] = useState<PosEntry | null>(null);
  const [posDrawerOpen, setPosDrawerOpen] = useState(false);

  const handlePosRowClick = (row: any) => {
    setSelectedPos(rowToPosEntry(row));
    setPosDrawerOpen(true);
  };

  const toggleSelection = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(
        new Set(filteredData.map((r: any) => r.id ?? r.entity_id ?? 0))
      );
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleListClick = (list: ActionList) => {
    setSelectedList(list);
    setListDetailOpen(true);
  };

  const handleActivityClick = useCallback((item: ActivityItem) => {
    setActivityDetailItem(item);
    setActivityDetailOpen(true);
  }, []);

  const activityUsers = useMemo(
    () => [...new Set(activity.map((a) => a.user))].sort(),
    [activity]
  );

  // Resolve market_id from the V2 context market name
  const marketId = useMemo(() => {
    if (!filters.market || !apiFilters.markets) return null;
    const found = (apiFilters.markets as any[]).find(
      (m: any) => m.name === filters.market || m.label === filters.market
    );
    return found ? [found.id ?? found.value] : null;
  }, [filters.market, apiFilters.markets]);

  // Build the full filter object merging context + POS filters
  const queryFilter = useMemo<V2DistributionPosFilter>(
    () => ({
      market_id:
        posFilters.markets.length > 0
          ? posFilters.markets.map((m) => Number(m.value))
          : marketId,
      continent_id:
        posFilters.continentId !== 'all'
          ? Number(posFilters.continentId)
          : null,
      country_id:
        posFilters.countryId !== 'all' ? Number(posFilters.countryId) : null,
      survey_year:
        posFilters.surveyYear !== 'all' ? posFilters.surveyYear : '9999',
      ...buildProductTypeQueryFields(posFilters, filters.productType === '2'),
      wine_type_id: Number(filters.productType),
      vintage:
        posFilters.vintage.length > 0
          ? posFilters.vintage.map((v) => String(v.value))
          : null,
      store_menu_price_band_id:
        posFilters.storePriceBand.length > 0
          ? posFilters.storePriceBand.map((p) => Number(p.value))
          : null,
      store_primary_sub_type_id:
        posFilters.storePrimaryType.length > 0
          ? posFilters.storePrimaryType.map((t) => Number(t.value))
          : null,
      store_ws_award:
        posFilters.storeWsAward.length > 0
          ? posFilters.storeWsAward.map((a) => String(a.value))
          : null,
      store_star:
        posFilters.storeMichelinStar.length > 0
          ? posFilters.storeMichelinStar.map((s) => String(s.value))
          : null,
      ...buildSpiritAwardQueryFields(posFilters),
      store_review_rating_id: null,
      selected_client_ref_ids:
        posFilters.clientRefs.length > 0
          ? posFilters.clientRefs.map((r) => Number(r.value))
          : null,
      selected_client_custom_ids:
        posFilters.clientCustomRefs.length > 0
          ? posFilters.clientCustomRefs.map((r) => Number(r.value))
          : null,
      rangePrices:
        posFilters.rangePrices[0] > 0 || posFilters.rangePrices[1] < 10000
          ? posFilters.rangePrices
          : null,
      pos_status:
        posFilters.posStatus !== 'all'
          ? (posFilters.posStatus as V2DistributionPosFilter['pos_status'])
          : null,
      search: posFilters.search || null
    }),
    [filters.productType, marketId, posFilters]
  );

  const isOnTrade = filters.channel === 'on_trade';

  const { data: posData = [], isLoading: isLoadingData } = useV2DistributionPos(
    queryFilter,
    isOnTrade
  );

  const { posCount } = useV2DistributionPosCount(queryFilter, isOnTrade);

  const { summary: posStatusSummary } = useV2DistributionPosStatusSummary(
    queryFilter,
    isOnTrade
  );

  // Search is handled by the API so pagination/counts stay aligned.
  const filteredData = useMemo(() => {
    return posData as any[];
  }, [posData]);

  const selectedPosEntries = useMemo(
    () =>
      filteredData
        .filter((r: any) => selectedIds.has(r.id ?? r.entity_id ?? 0))
        .map(rowToPosEntry),
    [filteredData, selectedIds]
  );

  const isLoading = isLoadingContext || isLoadingFilterOptions || isLoadingData;

  // Compute summary KPIs from data
  const myPosCount = posStatusSummary.active;

  const penetrationPct = useMemo(() => {
    if (!posCount) return 0;
    return Math.round((myPosCount / posCount) * 100);
  }, [myPosCount, posCount]);

  const posAtRisk = posStatusSummary.lost;

  const loyaltyPct = useMemo(() => {
    if (!myPosCount) return 0;
    return Math.round(((myPosCount - posAtRisk) / myPosCount) * 100);
  }, [myPosCount, posAtRisk]);

  const prospectCount = posStatusSummary.prospect;

  // ── New KPIs (from proto's header kpi-strip): Stability Index & Avg.
  // POS Rotation. Derived from the CRM pipeline's per-POS `stability`/
  // `evolution` signals until a dedicated aggregate is exposed by the API
  // until a dedicated aggregate is exposed by the API.
  const stabilityIndexPct = useMemo(() => {
    const withStability = crmPos.filter((p) => p.stability);
    if (!withStability.length) return null;
    const high = withStability.filter((p) => p.stability === 'high').length;
    return Math.round((high / withStability.length) * 100);
  }, [crmPos]);

  const avgRotationPct = useMemo(() => {
    if (!crmPos.length) return null;
    const moved = crmPos.filter((p) => p.evolution !== 0).length;
    return Math.round((moved / crmPos.length) * 100);
  }, [crmPos]);

  // Pagination (new — mirrors proto's pos-pager)
  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);
  const paginatedData = useMemo(
    () => filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredData, page]
  );

  // Build applied filter chips from posFilters
  const appliedChips = useMemo<AppliedChip[]>(() => {
    const chips: AppliedChip[] = [];
    posFilters.markets.forEach((m) =>
      chips.push({
        id: `market-${m.value}`,
        category: 'Market',
        label: String(m.label)
      })
    );
    if (posFilters.wineColor !== 'all') {
      const opt = [
        { value: '1', label: 'Red' },
        { value: '4', label: 'White' },
        { value: '2', label: 'Rosé' },
        { value: '5', label: 'Sparkling' },
        { value: '3', label: 'Sweet' },
        { value: '6', label: 'Fortified' }
      ].find((o) => o.value === posFilters.wineColor);
      chips.push({
        id: 'wineColor',
        category: 'Color',
        label: opt?.label || posFilters.wineColor
      });
    }
    posFilters.wineGrapeVariety.forEach((g) =>
      chips.push({
        id: `grape-${g.value}`,
        category: 'Grape',
        label: String(g.label)
      })
    );
    if (posFilters.wineCountry !== 'all') {
      const opt = (apiFilters.wine_countries || []).find(
        (c: any) => String(c.id) === posFilters.wineCountry
      );
      chips.push({
        id: 'wineCountry',
        category: 'Country',
        label: opt?.name || posFilters.wineCountry
      });
    }
    if (posFilters.wineRegion !== 'all') {
      const opt = (apiFilters.wine_regions || []).find(
        (r: any) => String(r.id) === posFilters.wineRegion
      );
      chips.push({
        id: 'wineRegion',
        category: 'Region',
        label: opt?.name || posFilters.wineRegion
      });
    }
    if (posFilters.wineFormatGroup !== 'all') {
      const opt = (apiFilters.wine_format_groups || []).find(
        (f: any) => String(f.id) === posFilters.wineFormatGroup
      );
      chips.push({
        id: 'wineFormatGroup',
        category: 'Format',
        label: opt?.name || posFilters.wineFormatGroup
      });
    }
    if (posFilters.wineAppelation !== 'all') {
      const opt = (apiFilters.wine_appelations || []).find(
        (a: any) => String(a.id) === posFilters.wineAppelation
      );
      chips.push({
        id: 'wineAppelation',
        category: 'Appellation',
        label: opt?.name || posFilters.wineAppelation
      });
    }
    posFilters.clientRefs.forEach((r) =>
      chips.push({
        id: `ref-${r.value}`,
        category: 'Ref',
        label: String(r.label)
      })
    );
    posFilters.clientCustomRefs.forEach((r) =>
      chips.push({
        id: `customRef-${r.value}`,
        category: 'Ref',
        label: String(r.label)
      })
    );
    posFilters.storePriceBand.forEach((p) =>
      chips.push({
        id: `price-${p.value}`,
        category: 'Price',
        label: String(p.label)
      })
    );
    posFilters.storePrimaryType.forEach((t) =>
      chips.push({
        id: `type-${t.value}`,
        category: 'Type',
        label: String(t.label)
      })
    );
    posFilters.storeWsAward.forEach((a) =>
      chips.push({
        id: `ws-${a.value}`,
        category: 'WS Rating',
        label: String(a.label)
      })
    );
    (
      [
        ['worldsBest', "World's 50 Best", posFilters.storeWorldsBest],
        ['topBar', 'Top bars', posFilters.storeTopBar],
        ['spiritedAward', 'Spirited Awards', posFilters.storeSpiritedAward]
      ] as const
    ).forEach(([key, category, options]) =>
      options.forEach((o) =>
        chips.push({
          id: `${key}-${o.value}`,
          category,
          label: String(o.label)
        })
      )
    );
    posFilters.spiritFormatSize.forEach((o) =>
      chips.push({
        id: `fmtSize-${o.value}`,
        category: 'Format',
        label: String(o.label)
      })
    );
    posFilters.storeMichelinStar.forEach((s) =>
      chips.push({
        id: `star-${s.value}`,
        category: 'Stars',
        label: String(s.label)
      })
    );
    if (posFilters.posStatus !== 'all') {
      chips.push({
        id: 'posStatus',
        category: 'Status',
        label: posFilters.posStatus === 'pos' ? 'Active' : 'Conquest'
      });
    }
    return chips;
  }, [posFilters, apiFilters]);

  const handleRemoveChip = useCallback((chipId: string) => {
    setPosFilters((prev) => {
      const next = { ...prev };
      if (chipId === 'wineColor') next.wineColor = 'all';
      else if (chipId === 'wineCountry') {
        next.wineCountry = 'all';
        next.wineRegion = 'all';
      } else if (chipId === 'wineRegion') next.wineRegion = 'all';
      else if (chipId === 'wineFormatGroup') next.wineFormatGroup = 'all';
      else if (chipId === 'wineAppelation') next.wineAppelation = 'all';
      else if (chipId === 'posStatus') next.posStatus = 'all';
      else if (chipId.startsWith('market-'))
        next.markets = next.markets.filter(
          (m) => `market-${m.value}` !== chipId
        );
      else if (chipId.startsWith('grape-'))
        next.wineGrapeVariety = next.wineGrapeVariety.filter(
          (g) => `grape-${g.value}` !== chipId
        );
      else if (chipId.startsWith('ref-'))
        next.clientRefs = next.clientRefs.filter(
          (r) => `ref-${r.value}` !== chipId
        );
      else if (chipId.startsWith('customRef-'))
        next.clientCustomRefs = next.clientCustomRefs.filter(
          (r) => `customRef-${r.value}` !== chipId
        );
      else if (chipId.startsWith('price-'))
        next.storePriceBand = next.storePriceBand.filter(
          (p) => `price-${p.value}` !== chipId
        );
      else if (chipId.startsWith('type-'))
        next.storePrimaryType = next.storePrimaryType.filter(
          (t) => `type-${t.value}` !== chipId
        );
      else if (chipId.startsWith('ws-'))
        next.storeWsAward = next.storeWsAward.filter(
          (a) => `ws-${a.value}` !== chipId
        );
      else if (chipId.startsWith('fmtSize-'))
        next.spiritFormatSize = next.spiritFormatSize.filter(
          (o) => `fmtSize-${o.value}` !== chipId
        );
      else if (chipId.startsWith('worldsBest-'))
        next.storeWorldsBest = next.storeWorldsBest.filter(
          (o) => `worldsBest-${o.value}` !== chipId
        );
      else if (chipId.startsWith('topBar-'))
        next.storeTopBar = next.storeTopBar.filter(
          (o) => `topBar-${o.value}` !== chipId
        );
      else if (chipId.startsWith('spiritedAward-'))
        next.storeSpiritedAward = next.storeSpiritedAward.filter(
          (o) => `spiritedAward-${o.value}` !== chipId
        );
      else if (chipId.startsWith('star-'))
        next.storeMichelinStar = next.storeMichelinStar.filter(
          (s) => `star-${s.value}` !== chipId
        );
      return next;
    });
  }, []);

  return (
    <Page>
      <PageHeader
        title="Points of Sale"
        subtitle="Every restaurant, retailer, and bar surveyed by Wine Services, with live competitive intelligence."
      />
      <V2ContextBar>
        <ContextToolbar>
          <FiltersBtn type="button" onClick={() => setFilterModalOpen(true)}>
            <FilterIcon />
            Filters
            <FiltersBadge $zero={activeFilterCount === 0}>
              {activeFilterCount}
            </FiltersBadge>
          </FiltersBtn>
          {activeFilterCount > 0 && (
            <ResetBtn type="button" onClick={handleFilterReset}>
              Reset
            </ResetBtn>
          )}
          {appliedChips.length > 0 && (
            <ChipsRow>
              {appliedChips.map((c) => (
                <AppliedChipEl key={c.id}>
                  <ChipCategory>{c.category}</ChipCategory>
                  {c.label}
                  <AppliedChipX
                    type="button"
                    onClick={() => handleRemoveChip(c.id)}
                  >
                    ×
                  </AppliedChipX>
                </AppliedChipEl>
              ))}
            </ChipsRow>
          )}
        </ContextToolbar>
      </V2ContextBar>

      {/* Summary KPI strip */}
      <SummaryStrip>
        <SummaryCard>
          <SummaryLabel>POS in scope</SummaryLabel>
          <SummaryValue>{posCount.toLocaleString()}</SummaryValue>
          <SummarySub>
            {filters.market || 'all markets'} ·{' '}
            {filters.channel === 'on_trade' ? 'on-trade' : 'off-trade'}
          </SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>My POS</SummaryLabel>
          <SummaryValue>{myPosCount.toLocaleString()}</SummaryValue>
          <SummarySub>{penetrationPct}% Penetration</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>POS at risk</SummaryLabel>
          <SummaryValue $alert>{posAtRisk.toLocaleString()}</SummaryValue>
          <SummarySub>{loyaltyPct}% loyalty</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>To win</SummaryLabel>
          <SummaryValue>{prospectCount.toLocaleString()}</SummaryValue>
          <SummarySub>prospect POS</SummarySub>
        </SummaryCard>
        {/* New KPIs from proto's page-header kpi-strip */}
        <SummaryCard>
          <SummaryLabel>Stability Index</SummaryLabel>
          <SummaryValue>
            {stabilityIndexPct !== null ? stabilityIndexPct : '—'}
            {stabilityIndexPct !== null && (
              <SummaryValueUnit>/100</SummaryValueUnit>
            )}
          </SummaryValue>
          <StabilityBar>
            <StabilityBarFill $pct={stabilityIndexPct ?? 0} />
          </StabilityBar>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Avg. POS Rotation</SummaryLabel>
          <SummaryValue>
            {avgRotationPct !== null ? avgRotationPct : '—'}
            {avgRotationPct !== null && <SummaryValueUnit>%</SummaryValueUnit>}
          </SummaryValue>
          <SummarySub>monthly turnover</SummarySub>
        </SummaryCard>
      </SummaryStrip>

      {/* Tabs */}
      <TabSection>
        <TabBar
          tabs={[
            {
              id: 'pos',
              label: `Points of Sale (${filteredData.length})`,
              icon: (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 9l1-5h16l1 5" />
                  <path d="M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" />
                  <path d="M3 9h18" />
                  <path d="M10 13h4" />
                </svg>
              )
            },
            {
              id: 'lists',
              label: `Action Lists (${actionLists.length})`,
              icon: (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              )
            },
            {
              id: 'activity',
              label: `Activity (${activity.length})`,
              icon: (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              )
            }
          ]}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as PosTab)}
        />
      </TabSection>

      {/* POS Tab */}
      {activeTab === 'pos' && (
        <TableWrap>
          {/* Toolbar — row count / sort summary + column display + export (new, from proto) */}
          <PosToolbar>
            <ToolbarLeft>
              Showing{' '}
              <strong>
                {filteredData.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
                {Math.min(page * PAGE_SIZE, filteredData.length)}
              </strong>{' '}
              of <strong>{filteredData.length}</strong> · Sorted by{' '}
              <strong>Evolution (desc)</strong>
            </ToolbarLeft>
            <ToolbarActions>
              <ColDisplayTrigger
                type="button"
                onClick={() => setColumnModalOpen(true)}
                aria-haspopup="dialog"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
                Column display{' '}
                <ColDisplayBadge>{visibleColumnCount}</ColDisplayBadge>
              </ColDisplayTrigger>
              <Button
                variant="primary"
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                }
              >
                Export .docx
              </Button>
            </ToolbarActions>
          </PosToolbar>

          {isLoading ? (
            <LoadingWrap>Loading points of sale...</LoadingWrap>
          ) : (
            <Table>
              <thead>
                <tr>
                  <ThCheckbox>
                    <RowCheck
                      $checked={
                        selectedIds.size === filteredData.length &&
                        filteredData.length > 0
                      }
                      $indeterminate={
                        selectedIds.size > 0 &&
                        selectedIds.size < filteredData.length
                      }
                      onClick={toggleSelectAll}
                    />
                  </ThCheckbox>
                  <Th $sort>POS</Th>
                  {columnVisibility.status && <Th>Current Status</Th>}
                  {columnVisibility.status12m && <Th>12 Months</Th>}
                  {columnVisibility.status6m && <Th>6 Months</Th>}
                  {columnVisibility.status3m && <Th>3 Months</Th>}
                  {columnVisibility.realtime && <Th>Real-time</Th>}
                  {columnVisibility.yourRef && <Th $align="right">Your Ref</Th>}
                  {columnVisibility.evolution && (
                    <Th $align="right">Evolution</Th>
                  )}
                  {columnVisibility.refs && (
                    <Th $align="right">Refs / Offered</Th>
                  )}
                  {columnVisibility.competitors && (
                    <Th $align="right">Competitors</Th>
                  )}
                  {columnVisibility.lastChange && <Th>Last Change</Th>}
                  {columnVisibility.market && <Th>Market</Th>}
                  {columnVisibility.avgPrice && (
                    <Th $align="right">Avg Price</Th>
                  )}
                  {columnVisibility.wsAward && <Th>WS Award</Th>}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row: any) => {
                  const id = row.id ?? row.entity_id ?? 0;
                  const evo = evolutionArrow(row.evolution);
                  const clientRefCount = countRefNames(row.client_refs);
                  const rivalRefCount = countRefNames(row.rival_refs);
                  return (
                    <Tr
                      key={id}
                      $selected={selectedIds.has(id)}
                      onClick={() => handlePosRowClick(row)}
                    >
                      <TdCheckbox>
                        <RowCheck
                          $checked={selectedIds.has(id)}
                          onClick={(e) => toggleSelection(id, e)}
                        />
                      </TdCheckbox>
                      <Td>
                        <PosNameCell>
                          <PosNameRow>
                            <PosName>{row.entity_name}</PosName>
                            {row.store_star && (
                              <StarIcons>
                                {Array.from({
                                  length: Number(row.store_star) || 1
                                }).map((_, i) => (
                                  <span key={i}>★</span>
                                ))}
                              </StarIcons>
                            )}
                          </PosNameRow>
                          <PosSubtitle>
                            {[
                              row.store_primary_sub_type_name,
                              row.store_city_name,
                              row.store_menu_price_band_name
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </PosSubtitle>
                        </PosNameCell>
                      </Td>
                      {columnVisibility.status && (
                        <Td>
                          <Badge variant={statusToVariant(row.pos_status)} dot>
                            {displayStatus(row.pos_status)}
                          </Badge>
                        </Td>
                      )}
                      {columnVisibility.status12m && (
                        <Td>
                          {row.status_12m ? (
                            <Badge
                              variant={statusToVariant(row.status_12m)}
                              dot
                            >
                              {row.status_12m}
                            </Badge>
                          ) : (
                            <NoData>—</NoData>
                          )}
                        </Td>
                      )}
                      {columnVisibility.status6m && (
                        <Td>
                          {row.status_6m ? (
                            <Badge variant={statusToVariant(row.status_6m)} dot>
                              {row.status_6m}
                            </Badge>
                          ) : (
                            <NoData>—</NoData>
                          )}
                        </Td>
                      )}
                      {columnVisibility.status3m && (
                        <Td>
                          {row.status_3m ? (
                            <Badge variant={statusToVariant(row.status_3m)} dot>
                              {row.status_3m}
                            </Badge>
                          ) : (
                            <NoData>—</NoData>
                          )}
                        </Td>
                      )}
                      {columnVisibility.realtime && (
                        <Td>
                          <RealtimeCell>
                            <LiveDot $on={Boolean(row.is_realtime)} />
                            {row.is_realtime === undefined
                              ? '—'
                              : row.is_realtime
                                ? 'Live'
                                : 'Periodic'}
                          </RealtimeCell>
                        </Td>
                      )}
                      {columnVisibility.yourRef && (
                        <TdNum>{clientRefCount || '—'}</TdNum>
                      )}
                      {columnVisibility.evolution && (
                        <TdNum>
                          <EvolutionCell $tone={evo.tone}>
                            {evo.arrow}{' '}
                            {row.evolution !== undefined &&
                            row.evolution !== null
                              ? `${evo.sign}${row.evolution}`
                              : '—'}
                          </EvolutionCell>
                        </TdNum>
                      )}
                      {columnVisibility.refs && (
                        <TdNum>
                          {clientRefCount || '—'}{' '}
                          <NoData>/ {rivalRefCount || '—'}</NoData>
                        </TdNum>
                      )}
                      {columnVisibility.competitors && (
                        <TdNum>{rivalRefCount || '—'}</TdNum>
                      )}
                      {columnVisibility.lastChange && (
                        <Td>
                          <LastChangeCell>
                            {row.last_change || '—'}
                          </LastChangeCell>
                        </Td>
                      )}
                      {columnVisibility.market && (
                        <Td>{row.market_name || '—'}</Td>
                      )}
                      {columnVisibility.avgPrice && (
                        <TdNum>
                          {row.av_price != null
                            ? `${row.av_price.toFixed(0)} ${
                                row.displayed_currency || 'USD'
                              }`
                            : '—'}
                        </TdNum>
                      )}
                      {columnVisibility.wsAward && (
                        <Td>{row.ws_award || '—'}</Td>
                      )}
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            onPrevious={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </TableWrap>
      )}

      {/* Action Lists Tab */}
      {activeTab === 'lists' && (
        <TableWrap>
          <TableHead>
            <span>
              <strong>{actionLists.length}</strong> saved action lists · Sorted
              by <strong>most recent</strong>
            </span>
            <SearchInput
              placeholder="Search by name, recipient, or action…"
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
            />
          </TableHead>
          <ActionListsGrid>
            {actionLists
              .filter(
                (l) =>
                  !searchQuery ||
                  l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  l.sentTo.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  l.action.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((list) => {
                const prog = listProgress(list);
                return (
                  <ActionListCard
                    key={list.id}
                    onClick={() => handleListClick(list)}
                  >
                    <ActionListCardHead>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <ActionListNameRow>{list.name}</ActionListNameRow>
                        <ActionListCardMeta>
                          <span>{formatListDate(list.createdAt)}</span>
                          <MetaDot />
                          <span>by {list.createdBy}</span>
                        </ActionListCardMeta>
                      </div>
                      <ActionListCountBadge>
                        {prog.total} POS
                      </ActionListCountBadge>
                    </ActionListCardHead>
                    <ActionListProgressRow>
                      <ActionListProgressStat $tone="pending">
                        <strong>{prog.toWinTotal}</strong>
                        <span>To Win</span>
                      </ActionListProgressStat>
                      <ActionListProgressStat $tone="positive">
                        <strong>{prog.active}</strong>
                        <span>Active</span>
                      </ActionListProgressStat>
                      <ActionListProgressStat $tone="negative">
                        <strong>{prog.lost}</strong>
                        <span>Lost</span>
                      </ActionListProgressStat>
                    </ActionListProgressRow>
                  </ActionListCard>
                );
              })}
          </ActionListsGrid>
        </TableWrap>
      )}

      {/* Action List Detail Modal */}
      {selectedList && (
        <Modal
          open={listDetailOpen}
          onClose={() => setListDetailOpen(false)}
          title={selectedList.name}
          subtitle={`Sent to ${selectedList.sentTo} · ${
            selectedList.action
          } · ${formatListDate(selectedList.createdAt)} by ${
            selectedList.createdBy
          }`}
          maxWidth="920px"
          footer={
            <DetailFooter>
              <Button onClick={() => setListDetailOpen(false)}>Close</Button>
            </DetailFooter>
          }
        >
          <DetailStatsStrip>
            <DetailStat>
              <DetailStatValue>
                {listProgress(selectedList).total}
              </DetailStatValue>
              <DetailStatLabel>Total POS</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue $color="pending">
                {listProgress(selectedList).toWinTotal}
              </DetailStatValue>
              <DetailStatLabel>To Win</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue $color="positive">
                {listProgress(selectedList).active}
              </DetailStatValue>
              <DetailStatLabel>Active</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue $color="negative">
                {listProgress(selectedList).lost}
              </DetailStatValue>
              <DetailStatLabel>Lost</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue>
                {listProgress(selectedList).successRate !== null
                  ? `${listProgress(selectedList).successRate}%`
                  : '—'}
              </DetailStatValue>
              <DetailStatLabel>Success rate</DetailStatLabel>
            </DetailStat>
          </DetailStatsStrip>
          <DetailTableWrap>
            <DetailTable>
              <thead>
                <tr>
                  <DetailTh>POS</DetailTh>
                  <DetailTh>City</DetailTh>
                  <DetailTh>Status at creation</DetailTh>
                  <DetailTh>Current status</DetailTh>
                  <DetailTh>Change</DetailTh>
                </tr>
              </thead>
              <tbody>
                {selectedList.pos.map((p) => {
                  const change = classifyChange(
                    p.initialStatus,
                    p.currentStatus
                  );
                  return (
                    <DetailTr
                      key={p.id}
                      $highlight={change.type === 'positive'}
                    >
                      <DetailTd>
                        <strong>{p.name}</strong>
                      </DetailTd>
                      <DetailTd $muted>{p.city}</DetailTd>
                      <DetailTd>
                        <Badge variant={statusToVariant(p.initialStatus)} dot>
                          {p.initialStatus === 'towin'
                            ? 'To Win'
                            : p.initialStatus}
                        </Badge>
                      </DetailTd>
                      <DetailTd>
                        <Badge variant={statusToVariant(p.currentStatus)} dot>
                          {p.currentStatus === 'towin'
                            ? 'To Win'
                            : p.currentStatus}
                        </Badge>
                      </DetailTd>
                      <DetailTd>
                        <ChangePill $tone={change.type}>
                          {change.label}
                        </ChangePill>
                      </DetailTd>
                    </DetailTr>
                  );
                })}
              </tbody>
            </DetailTable>
          </DetailTableWrap>
        </Modal>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <TableWrap>
          <TableHead>
            <span>
              <strong>{activity.length}</strong> recent activities
            </span>
            <SearchInput
              placeholder="Search by user or target…"
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
            />
            <ActivityFilterRow>
              <ActivityFilterLabel>Filter by user</ActivityFilterLabel>
              <ActivityUserSelect
                value={activityUserFilter}
                onChange={(e) => setActivityUserFilter(e.target.value)}
              >
                <option value="all">All users</option>
                {activityUsers.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </ActivityUserSelect>
            </ActivityFilterRow>
          </TableHead>
          <ActivityFeed
            items={activity
              .filter(
                (a) =>
                  (activityUserFilter === 'all' ||
                    a.user === activityUserFilter) &&
                  (!searchQuery ||
                    a.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    a.target.toLowerCase().includes(searchQuery.toLowerCase()))
              )
              .map(
                (a): ActivityItem => ({
                  id: a.id,
                  initials: a.userInitials,
                  user: a.user,
                  kind: a.kind,
                  action: a.action,
                  target: a.target,
                  detail: a.detail,
                  when: a.when,
                  privacy: a.privacy,
                  with_: a.with_,
                  posCount: a.posCount,
                  sentTo: a.sentTo,
                  recipientCount: a.recipientCount
                })
              )}
            onItemClick={handleActivityClick}
          />
        </TableWrap>
      )}

      {/* Selection Bar */}
      <SelectionBar $visible={selectedIds.size > 0 && activeTab === 'pos'}>
        <SelectionCount>
          <strong>{selectedIds.size}</strong> POS selected
        </SelectionCount>
        <SelectionDivider />
        <SelectionClear onClick={clearSelection}>Clear</SelectionClear>
        <SelectionAction type="button" onClick={() => setEmailModalOpen(true)}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            <polyline points="22 6 12 13 2 6" />
          </svg>
          Write an email
        </SelectionAction>
        <SelectionSave
          type="button"
          onClick={() => setActionListModalOpen(true)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
          Save as Action List
        </SelectionSave>
      </SelectionBar>

      {/* Activity Detail Drawer */}
      <ActivityDetailDrawer
        item={activityDetailItem}
        open={activityDetailOpen}
        onClose={() => setActivityDetailOpen(false)}
      />

      {/* POS Detail Drawer — mirrors proto's canonical #crm-drawer, opened
          on any POS row click (proto's body-level `tr[data-id]` handler). */}
      <PosDrawer
        pos={selectedPos}
        open={posDrawerOpen}
        onClose={() => setPosDrawerOpen(false)}
      />

      {/* POS Filter Modal */}
      <PosFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Points of Sale"
        posCount={posCount}
        filters={posFilters}
        onChange={handleFilterChange}
        onReset={handleFilterReset}
        currency={posData[0]?.displayed_currency || 'USD'}
      />

      {/* Column Display Modal (new — from proto's posColDisplayModal) */}
      <ColumnDisplayModal
        open={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        columns={POS_COLUMNS}
        visible={columnVisibility}
        onApply={setColumnVisibility}
        title="Points of Sale · Column display"
      />

      {/* Selection Bar modals — mirrors CrmSalesTracking's "Write an email" /
          "Save as Action List" flows for POS selected from this page. */}
      <WriteEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        selectedPos={selectedPosEntries}
        onSend={() => {
          clearSelection();
        }}
      />
      <SaveActionListModal
        open={actionListModalOpen}
        onClose={() => setActionListModalOpen(false)}
        selectedPos={selectedPosEntries}
        onSave={(data) => {
          createActionList.mutate(
            toCreateActionListBody({
              name: data.name,
              action: data.action,
              notes: data.notes,
              sentTo: data.sentTo,
              sentToType: data.sentToType,
              privacy: data.privacy,
              storeIds: selectedPosEntries.map((p) => p.id)
            }),
            {
              onSuccess: () => {
                refetchCrmPipeline();
                clearSelection();
                setActionListModalOpen(false);
              },
              onError: (error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to save action list:', error);
              }
            }
          );
        }}
      />
    </Page>
  );
};

// --- Styled ---

const Page = styled.div`
  max-width: 1400px;
`;

const LoadingWrap = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-top: 16px;
  background: var(--white);
  border-radius: 14px;
  border: 1px solid var(--line-soft, #e8e5df);
  overflow: hidden;

  @media (min-width: 1200px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const SummaryCard = styled.div`
  padding: 24px 28px;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  &:not(:last-child) {
    border-right: 1px solid var(--line-soft, #e8e5df);
  }
  @media (min-width: 1200px) {
    border-bottom: none;
  }
`;

const SummaryValueUnit = styled.span`
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 500;
  color: var(--ink-faint);
`;

const StabilityBar = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: var(--line-soft, #e8e5df);
  margin-top: 10px;
  overflow: hidden;
`;

const StabilityBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
  background: var(--olive, #7a8c5f);
`;

const SummaryLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 8px;
`;

const SummaryValue = styled.div<{ $alert?: boolean }>`
  font-family: var(--font-serif, 'Fraunces', serif);
  font-size: 36px;
  font-weight: 700;
  color: ${({ $alert }) =>
    $alert ? 'var(--terracotta, #c84b1f)' : 'var(--ink)'};
  line-height: 1.1;
`;

const SummarySub = styled.div`
  font-size: 13px;
  color: var(--ink-muted);
  margin-top: 6px;
`;

const TabSection = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;
`;

const TableWrap = styled.div`
  background: #fff;
  border-radius: 14px;
  border: 1px solid var(--line-soft, #e8e5df);
  overflow: hidden;
`;

const TableHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  font-size: 13px;
  color: var(--ink-muted);
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  strong {
    color: var(--ink);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const ThCheckbox = styled.th`
  width: 48px;
  padding: 14px 0 14px 20px;
  vertical-align: middle;
`;

const Th = styled.th<{ $align?: string; $sort?: boolean }>`
  padding: 14px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-faint);
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  white-space: nowrap;
  ${({ $sort }) =>
    $sort &&
    `
    &::after {
      content: ' ↓';
      opacity: 0.5;
    }
  `}
`;

const Tr = styled.tr<{ $selected?: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) =>
    $selected ? 'var(--terracotta-soft, #fef3ee)' : 'transparent'};
  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'var(--terracotta-soft, #fef3ee)' : 'var(--paper, #f9f7f4)'};
  }
  &:not(:last-child) td {
    border-bottom: 1px solid var(--line-soft, #e8e5df);
  }
`;

const TdCheckbox = styled.td`
  width: 48px;
  padding: 14px 0 14px 20px;
  vertical-align: middle;
`;

const Td = styled.td`
  padding: 14px 16px;
  vertical-align: middle;
`;

const TdNum = styled.td`
  padding: 14px 16px;
  text-align: right;
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
`;

const RowCheck = styled.div<{ $checked?: boolean; $indeterminate?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid
    ${({ $checked, $indeterminate }) =>
      $checked || $indeterminate
        ? 'var(--terracotta, #c84b1f)'
        : 'var(--line-mid, #d4d0ca)'};
  background: ${({ $checked, $indeterminate }) =>
    $checked || $indeterminate ? 'var(--terracotta, #c84b1f)' : '#fff'};
  cursor: pointer;
  position: relative;
  transition:
    border-color 0.15s,
    background 0.15s;
  &:hover {
    border-color: ${({ $checked, $indeterminate }) =>
      $checked || $indeterminate
        ? 'var(--terracotta, #c84b1f)'
        : 'var(--ink-muted)'};
  }
  &::after {
    content: '${({ $checked, $indeterminate }) =>
      $checked ? '✓' : $indeterminate ? '–' : ''}';
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const PosNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PosNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PosName = styled.span`
  font-weight: 600;
  font-size: 13.5px;
  color: var(--ink);
`;

const StarIcons = styled.span`
  color: var(--amber, #d4a017);
  font-size: 12px;
  letter-spacing: -1px;
  line-height: 1;
`;

const PosSubtitle = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
  line-height: 1.3;
`;

// --- Toolbar (new — row count / column display / export) ---

const PosToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
`;

const ToolbarLeft = styled.div`
  font-size: 13px;
  color: var(--ink-muted);
  strong {
    color: var(--ink);
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ColDisplayTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border-radius: 7px;
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--ink);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  svg {
    width: 14px;
    height: 14px;
  }
  &:hover {
    border-color: var(--ink);
  }
`;

const ColDisplayBadge = styled.span`
  background: var(--terracotta-soft, #fef3ee);
  color: var(--terracotta, #c84b1f);
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
`;

// --- New table cells (12/6/3 months, realtime, evolution, last change) ---

const NoData = styled.span`
  color: var(--ink-faint);
`;

const RealtimeCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--ink-muted);
`;

const LiveDot = styled.span<{ $on?: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $on }) =>
    $on ? 'var(--olive, #7a8c5f)' : 'var(--line-mid, #d4d0ca)'};
`;

const EvolutionCell = styled.span<{ $tone: 'up' | 'down' | 'flat' }>`
  font-weight: 600;
  color: ${({ $tone }) =>
    $tone === 'up'
      ? 'var(--olive, #7a8c5f)'
      : $tone === 'down'
        ? 'var(--crimson, #c0392b)'
        : 'var(--ink-faint)'};
`;

const LastChangeCell = styled.span`
  color: var(--ink-muted);
  font-size: 12.5px;
`;

// --- Action Lists ---

const ActionListsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding: 20px;
`;

const ActionListCard = styled.div`
  background: var(--paper, #f9f7f4);
  border: 1px solid var(--line-soft, #e8e5df);
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  transition: box-shadow 0.15s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const ActionListCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const ActionListNameRow = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--ink);
`;

const ActionListCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: var(--ink-faint);
  margin-top: 3px;
`;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--ink-faint);
`;

const ActionListCountBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  background: var(--terracotta-soft, #fef3ee);
  color: var(--terracotta, #c84b1f);
  border-radius: 6px;
  padding: 3px 8px;
  white-space: nowrap;
`;

const ActionListProgressRow = styled.div`
  display: flex;
  gap: 16px;
`;

const ActionListProgressStat = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  strong {
    color: ${({ $tone }) =>
      $tone === 'positive'
        ? 'var(--olive)'
        : $tone === 'negative'
          ? 'var(--crimson)'
          : 'var(--amber)'};
  }
  span {
    color: var(--ink-faint);
  }
`;

// --- Detail Modal ---

const DetailFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DetailStatsStrip = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  margin-bottom: 16px;
`;

const DetailStat = styled.div`
  text-align: center;
`;

const DetailStatValue = styled.div<{ $color?: string }>`
  font-family: var(--font-serif, 'Fraunces', serif);
  font-size: 22px;
  font-weight: 700;
  color: ${({ $color }) =>
    $color === 'positive'
      ? 'var(--olive)'
      : $color === 'negative'
        ? 'var(--crimson)'
        : $color === 'pending'
          ? 'var(--amber)'
          : 'var(--ink)'};
`;

const DetailStatLabel = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
`;

const DetailTableWrap = styled.div`
  overflow-x: auto;
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const DetailTh = styled.th`
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ink-faint);
  border-bottom: 1px solid var(--line-soft, #e8e5df);
`;

const DetailTr = styled.tr<{ $highlight?: boolean }>`
  background: ${({ $highlight }) =>
    $highlight ? 'var(--olive-soft, #f0f5e8)' : 'transparent'};
  &:not(:last-child) td {
    border-bottom: 1px solid var(--line-soft, #e8e5df);
  }
`;

const DetailTd = styled.td<{ $muted?: boolean }>`
  padding: 10px 12px;
  color: ${({ $muted }) => ($muted ? 'var(--ink-faint)' : 'var(--ink)')};
`;

const ChangePill = styled.span<{ $tone: string }>`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ $tone }) =>
    $tone === 'positive'
      ? 'var(--olive-soft, #f0f5e8)'
      : $tone === 'negative'
        ? 'var(--crimson-soft, #fef0f0)'
        : $tone === 'pending'
          ? 'var(--amber-soft, #fef8e8)'
          : 'var(--paper, #f9f7f4)'};
  color: ${({ $tone }) =>
    $tone === 'positive'
      ? 'var(--olive)'
      : $tone === 'negative'
        ? 'var(--crimson)'
        : $tone === 'pending'
          ? 'var(--amber)'
          : 'var(--ink-muted)'};
`;

// --- Activity ---

const ActivityFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const ActivityFilterLabel = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
`;

const ActivityUserSelect = styled.select`
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--line-soft, #e8e5df);
  background: #fff;
  color: var(--ink);
`;

// --- Selection Bar ---

const SelectionBar = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%)
    translateY(${({ $visible }) => ($visible ? '0' : '120%')});
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--ink, #1a1a1a);
  color: #fff;
  padding: 12px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  font-size: 13px;
  transition: transform 0.2s ease;
  z-index: 100;
`;

const SelectionCount = styled.span`
  strong {
    font-weight: 700;
  }
`;

const SelectionDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
`;

const SelectionClear = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  cursor: pointer;
  &:hover {
    color: #fff;
  }
`;

const SelectionAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;

const SelectionSave = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--terracotta);
  color: var(--white);
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s;
  white-space: nowrap;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    background: var(--terracotta-dark, #b0411a);
    transform: translateY(-1px);
  }
`;
